import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuthContext } from '@/contexts/AuthContext';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { User } from '@supabase/supabase-js';
import { useNavigate } from 'react-router-dom';
import { 
  Upload, 
  Camera, 
  Globe, 
  Bell, 
  User as UserIcon,
  Settings,
  Clock,
  Mail,
  Calendar,
  CheckCircle,
  AlertCircle,
  Edit2,
  Save
} from 'lucide-react';
import UserTimezoneSettings from '@/components/user/UserTimezoneSettings';

interface UserProfile {
  id: string;
  email: string;
  role: string;
  created_at: string;
  updated_at: string;
  is_active: boolean;
  profile_image_file_url?: string;
}

export default function Profile() {
  const { user, signOut, uploadAvatar, loading } = useAuthContext();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lastFetchedUserId = useRef<string | null>(null);

  // Memoizar a função fetchAvatarImage para evitar recriações
  const fetchAvatarImage = useCallback(async (filePath: string) => {
    try {
      console.log('Profile - Tentando buscar avatar com caminho:', filePath);
      
      // Caminho correto para o bucket avatars/users
      const { data, error } = await supabase
        .storage
        .from('avatars')
        .download(`users/${filePath.split('/').pop()}`);
        
      if (error) {
        console.error('Erro ao baixar avatar:', error);
        return;
      }
      
      // Criar URL do objeto blob para exibição da imagem
      const url = URL.createObjectURL(data);
      setAvatarUrl(url);
      console.log('Avatar carregado com sucesso');
    } catch (error) {
      console.error('Erro ao processar avatar:', error);
    }
  }, []);

  // Memoizar a função fetchUserProfile para evitar recriações
  const fetchUserProfile = useCallback(async (user: User) => {
    try {
      setPageLoading(true);
      console.log('🔍 Buscando perfil para o usuário:', user.id);
      
      // Primeiro, verificar se o usuário existe na tabela auth.users (que é gerenciada pelo Supabase Auth)
      const { data: authUser, error: authError } = await supabase.auth.getUser();
      
      if (authError) {
        console.error('❌ Erro ao verificar usuário auth:', authError);
        throw authError;
      }
      
      if (!authUser || !authUser.user) {
        console.error('❌ Usuário não encontrado no auth');
        throw new Error('Usuário não encontrado');
      }
      
      // Buscar o perfil do usuário na tabela users
      const { data: profileData, error: profileError } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single();
      
      if (profileError && profileError.code !== 'PGRST116') { // Ignorar erro "não encontrado"
        console.warn('⚠️ Erro ao buscar perfil na tabela users:', profileError);
      }
      
      // Verificar se usuário está inativo
      if (profileData && profileData.is_active === false) {
        console.log('🚫 Usuário inativo detectado no Profile, fazendo logout');
        await signOut();
        return;
      }
      
      // Criar um perfil padrão caso não exista
      const userProfile = profileData || {
        id: user.id,
        email: user.email,
        is_active: true,
        created_at: new Date().toISOString(),
        role: 'user'
      };
      
      console.log('✅ Perfil carregado:', userProfile);
      setProfile(userProfile);
      
      // Se houver um caminho de arquivo, baixar a imagem
      if (userProfile.profile_image_file_url) {
        await fetchAvatarImage(userProfile.profile_image_file_url);
      }
    } catch (error) {
      console.error('❌ Erro ao buscar perfil:', error);
      toast({
        variant: "destructive",
        title: "Erro ao carregar perfil",
        description: "Não foi possível carregar os dados do perfil. Tente atualizar a página.",
      });
    } finally {
      setPageLoading(false);
    }
  }, [fetchAvatarImage, signOut, toast]);

  // Executar apenas uma vez quando o usuário mudar
  useEffect(() => {
    if (user && user.id !== lastFetchedUserId.current) {
      fetchUserProfile(user);
      lastFetchedUserId.current = user.id;
    }
  }, [user, fetchUserProfile]);

  // Monitor user state changes for logout
  useEffect(() => {
    if (!user && !loading) {
      console.log('🚪 Profile: User logged out, redirecting to login...');
      navigate('/login');
    }
  }, [user, loading, navigate]);



  // Memoizar a função handleAvatarClick para evitar recriações
  const handleAvatarClick = useCallback(() => {
    // Acionar o input de arquivo quando o avatar for clicado
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  // Memoizar a função handleFileChange para evitar recriações
  const handleFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    const file = files[0];
    // Validar tipo de arquivo (apenas imagens)
    if (!file.type.startsWith('image/')) {
      toast({
        variant: 'destructive',
        title: 'Tipo de arquivo inválido',
        description: 'Por favor, selecione uma imagem (JPG, PNG, etc.).',
      });
      return;
    }
    
    // Validar tamanho do arquivo (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        variant: 'destructive',
        title: 'Arquivo muito grande',
        description: 'A imagem deve ter no máximo 5MB.',
      });
      return;
    }
    
    try {
      setUploadingAvatar(true);
      
      // Fazer upload do avatar usando a função do hook
      const imageUrl = await uploadAvatar(file);
      
      // Atualizar o perfil local com a nova URL
      if (profile) {
        setProfile({
          ...profile,
          profile_image_file_url: imageUrl
        });
      }
      
      toast({
        title: 'Avatar atualizado',
        description: 'Sua foto de perfil foi atualizada com sucesso.',
      });
    } catch (error: any) {
      console.error('Erro no upload do avatar:', error);
      toast({
        variant: 'destructive',
        title: 'Erro no upload',
        description: error.message || 'Não foi possível fazer o upload da imagem.',
      });
    } finally {
      setUploadingAvatar(false);
      // Limpar o input de arquivo
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [profile, uploadAvatar, toast]);

  // Memoizar a função formatDate para evitar recriações
  const formatDate = useCallback((dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Data inválida';
    }
  }, []);

  // Memoizar a função getRoleName para evitar recriações
  const getRoleName = useCallback((role: string) => {
    switch(role) {
      case 'admin': return 'Administrador';
      case 'user': return 'Usuário';
      case 'support': return 'Suporte';
      case 'sales': return 'Vendas';
      case 'finance': return 'Financeiro';
      case 'marketing': return 'Marketing';
      default: return role || 'Usuário';
    }
  }, []);

  // Memoizar a função getInitials para evitar recriações
  const getInitials = useCallback((email: string) => {
    return email.substring(0, 2).toUpperCase();
  }, []);

  // Memoizar a função handleLogout para evitar recriações
  const handleLogout = useCallback(async () => {
    if (loggingOut) return; // Prevent multiple clicks
    
    try {
      setLoggingOut(true);
      console.log('🚪 Iniciando logout...');
      await signOut();
      console.log('✅ Logout realizado com sucesso');
      
      // Mostrar mensagem de sucesso
      toast({
        title: 'Logout realizado',
        description: 'Você foi desconectado com sucesso.',
      });
      
      // Redirecionar para a página de login após um pequeno delay
      setTimeout(() => {
        navigate('/login');
      }, 1000);
      
    } catch (error: any) {
      console.error('❌ Erro durante o logout:', error);
      toast({
        variant: 'destructive',
        title: 'Erro no logout',
        description: error.message || 'Não foi possível fazer logout. Tente novamente.',
      });
    } finally {
      setLoggingOut(false);
    }
  }, [signOut, navigate, toast, loggingOut]);



  if (pageLoading) {
    return (
      <>
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
          {/* Hero Header Skeleton */}
          <div className="relative overflow-hidden" style={{ backgroundColor: '#1F1746' }}>
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
              <div className="flex flex-col md:flex-row items-center justify-between">
                <div className="flex items-center space-x-6 mb-6 md:mb-0">
                  {/* Avatar Skeleton */}
                  <div className="relative">
                    <div className="h-20 w-20 md:h-24 md:w-24 bg-white/20 rounded-full animate-pulse"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="animate-spin h-8 w-8 border-2 border-white/30 rounded-full border-t-white"></div>
                    </div>
                  </div>
                  
                  {/* User Info Skeleton */}
                  <div className="text-center md:text-left">
                    <div className="h-8 w-32 bg-white/20 rounded animate-pulse mb-3"></div>
                    <div className="flex flex-col md:flex-row items-center gap-2 mb-3">
                      <div className="h-6 w-20 bg-white/20 rounded animate-pulse"></div>
                      <div className="h-6 w-16 bg-white/20 rounded animate-pulse"></div>
                    </div>
                    <div className="h-4 w-40 bg-white/20 rounded animate-pulse"></div>
                  </div>
                </div>
                
                {/* Action Buttons Skeleton */}
                <div className="flex items-center space-x-3">
                  <div className="h-10 w-32 bg-white/20 rounded animate-pulse"></div>
                  <div className="h-10 w-20 bg-white/20 rounded animate-pulse"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Skeleton */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Tabs Skeleton */}
            <div className="space-y-8">
              <div className="flex justify-center md:justify-start">
                <div className="h-12 w-64 bg-white/60 rounded-lg animate-pulse"></div>
              </div>
              
              {/* Profile Cards Skeleton */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="bg-white/60 backdrop-blur-sm border shadow-sm rounded-lg p-6">
                    <div className="flex items-center space-x-3">
                      <div className="h-12 w-12 bg-gray-200 rounded-full animate-pulse"></div>
                      <div className="space-y-2">
                        <div className="h-4 w-20 bg-gray-200 rounded animate-pulse"></div>
                        <div className="h-5 w-24 bg-gray-200 rounded animate-pulse"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Detailed Information Skeleton */}
              <div className="bg-white/60 backdrop-blur-sm border shadow-sm rounded-lg">
                <div className="p-6 border-b">
                  <div className="h-6 w-48 bg-gray-200 rounded animate-pulse mb-2"></div>
                  <div className="h-4 w-64 bg-gray-200 rounded animate-pulse"></div>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      {[1, 2].map((i) => (
                        <div key={i} className="p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className="h-5 w-5 bg-gray-200 rounded animate-pulse"></div>
                            <div className="space-y-2">
                              <div className="h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
                              <div className="h-4 w-32 bg-gray-200 rounded animate-pulse"></div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="space-y-4">
                      {[1, 2].map((i) => (
                        <div key={i} className="p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className="h-5 w-5 bg-gray-200 rounded animate-pulse"></div>
                            <div className="space-y-2">
                              <div className="h-4 w-28 bg-gray-200 rounded animate-pulse"></div>
                              <div className="h-4 w-36 bg-gray-200 rounded animate-pulse"></div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!user) {
    return (
      <>
        <div className="w-full py-6">
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <p className="text-red-500 mb-4">Usuário não encontrado. Faça login novamente.</p>
              <Button 
                onClick={() => navigate('/login')}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Ir para Login
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>CSM | Trivium - Perfil</title>
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
        {/* Hero Header */}
        <div className="relative overflow-hidden" style={{ backgroundColor: '#1F1746' }}>
          {/* SVG Background Element */}
          <div className="absolute inset-0 pointer-events-none">
            <svg 
              className="opacity-30"
              width="1690"
              height="1026.5433"
              viewBox="0 0 826.54 502.06" 
              xmlns="http://www.w3.org/2000/svg"
              preserveAspectRatio="xMidYMid slice"
            >
              <g transform="scale(1.1) translate(-70, -315)">
                <path 
                  fill="rgba(255, 255, 255, 0.25)" 
                  d="M28.31,502.06l-22.83-33.16c2.83-1.95,5.72-3.95,8.66-6,29.97-20.81,67.28-46.72,119.43-65.67,35.27-12.82,72.85-20.86,113.48-24.23-6.13-3.25-12.27-6.69-18.3-10.34-19.51-11.8-35.53-24.3-47.63-37.15-15.6-16.58-25.21-34.28-28.58-52.62-.7-3.82-1.29-7.59-1.76-11.3-11.77-1.09-23.46-3.87-34.84-8.32-29.68-11.61-57.34-34.34-82.21-67.54-13.34-17.81-22.86-38.24-28.3-60.72C1.09,107.05-.66,87.72.22,67.56,1.7,33.97,10.17,7.36,12.73,0l38.02,13.24c-2.11,6.05-9.07,27.97-10.31,56.09-1.6,36.48,6.98,67.53,25.51,92.26,20.31,27.11,42.06,45.34,64.66,54.18,6.26,2.45,12.56,4.16,18.88,5.14,1.32-20.67,5.43-38.28,9.63-51.53,9.97-31.47,26.22-57.23,45.76-72.54,9.06-7.1,18.58-11.71,28.29-13.72,11.03-2.28,22.22-1.07,32.37,3.51,11.52,5.2,20.52,13.57,26.04,24.2,4.97,9.58,7.07,20.63,6.23,32.85-1.49,21.74-12.62,46.21-30.53,67.15-18.86,22.04-43.6,38.42-69.67,46.12-2.19.65-4.38,1.23-6.58,1.76.32,2.31.69,4.61,1.11,6.89,1.95,10.6,8.11,21.47,18.31,32.31,9.59,10.19,22.76,20.38,39.14,30.29,30.76,18.6,65.94,31.97,84.83,39.15,5.09,1.93,9.11,3.46,12.1,4.72,2.14.9,3.69,1.63,5.03,2.37,10.47,5.81,12.4,15.94,10.86,22.84-1.65,7.43-8.22,15.26-19.16,16.01-1.57.11-3.22.08-5.37-.08-15.91-1.19-31.04-1.75-45.41-1.75-133.69,0-203.21,48.29-255.36,84.5-2.99,2.08-5.92,4.11-8.8,6.09ZM244.08,122.3c-4.25,0-9.06,2.09-14.37,6.25-12.84,10.05-24.88,29.86-32.2,52.99-3.62,11.43-6.04,23.4-7.24,35.51,17.22-5.98,33.57-17.36,46.43-32.38,12.23-14.29,20.06-30.63,20.95-43.72.91-13.29-5.8-16.32-8.67-17.61-1.53-.69-3.17-1.04-4.89-1.04Z"
                />
                <path 
                  fill="rgba(255, 255, 255, 0.25)" 
                  d="M491.98,0l-22.83,33.16c2.83,1.95,5.72,3.95,8.66,6,29.97,20.81,67.28,46.72,119.43,65.67,35.27,12.82,72.85,20.86,113.48,24.23-6.13,3.25-12.27,6.69-18.3,10.34-19.51,11.8-35.53,24.3-47.63,37.15-15.6,16.58-25.21,34.28-28.58,52.62-.7,3.82-1.29,7.59-1.76,11.3-11.77,1.09-23.46,3.87-34.84,8.32-29.68,11.61-57.34,34.34-82.21,67.54-13.34,17.81-22.86,38.24-28.3,60.72-4.34,17.96-6.1,37.29-5.21,57.45,1.48,33.59,9.95,60.2,12.51,67.56l38.02-13.24c-2.11-6.05-9.07-27.97-10.31-56.09-1.6-36.48,6.98-67.53,25.51-92.26,20.31-27.11,42.06-45.34,64.66-54.18,6.26-2.45,12.56-4.16,18.88-5.14,1.32,20.67,5.43,38.28,9.63,51.53,9.97,31.47,26.22,57.23,45.76,72.54,9.06,7.1,18.58,11.71,28.29,13.72,11.03,2.28,22.22,1.07,32.37-3.51,11.52-5.2,20.52-13.57,26.04-24.2,4.97-9.58,7.07-20.63,6.23-32.85-1.49-21.74-12.62-46.21-30.53-67.15-18.86-22.04-43.6-38.42-69.67-46.12-2.19-.65-4.38-1.23-6.58-1.76.32-2.31.69-4.61,1.11-6.89,1.95-10.6,8.11-21.47,18.31-32.31,9.59-10.19,22.76-20.38,39.14-30.29,30.76-18.6,65.94-31.97,84.83-39.15,5.09-1.93,9.11-3.46,12.1-4.72,2.14-.9,3.69-1.63,5.03-2.37,10.47-5.81,12.4-15.94,10.86-22.84-1.65-7.43-8.22-15.26-19.16-16.01-1.57-.11-3.22-.08-5.37.08-15.91,1.19-31.04,1.75-45.41,1.75-133.69,0-203.21-48.29-255.36-84.5-2.99-2.08-5.92-4.11-8.8-6.09ZM707.74,379.76c-4.25,0-9.06-2.09-14.37-6.25-12.84-10.05-24.88-29.86-32.2-52.99-3.62-11.43-6.04-23.4-7.24-35.51,17.22,5.98,33.57,17.36,46.43,32.38,12.23,14.29,20.06,30.63,20.95,43.72.91,13.29-5.8,16.32-8.67,17.61-1.53.69-3.17,1.04-4.89,1.04Z"
                />
              </g>
            </svg>
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-black/10"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="flex items-center space-x-6 mb-6 md:mb-0">
                {/* Enhanced Avatar Section */}
                <div className="relative group">
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                  
                  <div className="relative" onClick={handleAvatarClick}>
                    <Avatar className="h-20 w-20 md:h-24 md:w-24 cursor-pointer ring-4 ring-white/20 transition-all duration-300 group-hover:ring-white/40 group-hover:scale-105">
                      <AvatarImage 
                        src={avatarUrl || ""} 
                        alt={profile?.email} 
                        onError={(e) => {
                          console.log('Profile - Erro ao carregar a imagem:', profile?.profile_image_file_url);
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                      <AvatarFallback className="text-xl bg-white/10 text-white backdrop-blur-sm">
                        {profile?.email ? getInitials(profile.email) : 'U'}
                      </AvatarFallback>
                    </Avatar>
                    
                    {/* Overlay with Camera Icon */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                      <div className="bg-black/60 backdrop-blur-sm rounded-full p-3 transform transition-transform duration-300 group-hover:scale-110">
                        {uploadingAvatar ? (
                          <div className="animate-spin h-5 w-5 border-2 border-white rounded-full border-t-transparent" />
                        ) : (
                          <Camera className="h-5 w-5 text-white" />
                        )}
                      </div>
                    </div>
                    
                    {/* Upload Status Badge */}
                    {uploadingAvatar && (
                      <div className="absolute -bottom-2 -right-2">
                        <Badge className="bg-blue-500 text-white animate-pulse">
                          Enviando...
                        </Badge>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* User Info */}
                <div className="text-center md:text-left">
                  <h1 className="text-2xl md:text-3xl font-extrabold text-white mb-2">
                    {profile?.email?.split('@')[0] || 'Usuário'}
                  </h1>
                  <div className="flex flex-col md:flex-row items-center gap-2 mb-3">
                    <Badge 
                      variant="secondary" 
                      className="bg-white/20 text-white border-white/30 backdrop-blur-sm"
                    >
                      <UserIcon className="h-3 w-3 mr-1" />
                      {profile?.role ? getRoleName(profile.role) : 'Usuário'}
                    </Badge>
                    <Badge 
                      className={`${
                        profile?.is_active 
                          ? 'bg-green-500/20 text-green-100 border-green-400/30' 
                          : 'bg-yellow-500/20 text-yellow-100 border-yellow-400/30'
                      } backdrop-blur-sm`}
                    >
                      {profile?.is_active ? (
                        <>
                          <CheckCircle className="h-3 w-3 mr-1" /> Ativo
                        </>
                      ) : (
                        <>
                          <AlertCircle className="h-3 w-3 mr-1" /> Inativo
                        </>
                      )}
                    </Badge>
                  </div>
                  <p className="text-white/80 text-sm flex items-center justify-center md:justify-start">
                    <Mail className="h-4 w-4 mr-2" />
                    {profile?.email}
                  </p>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex items-center space-x-3">
                <Button 
                  variant="outline" 
                  className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm transition-all duration-300"
                  onClick={handleAvatarClick}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Alterar Foto
                </Button>
                <Button 
                  variant="destructive" 
                  className="bg-red-500/20 border-red-400/30 text-red-100 hover:bg-red-500/30 backdrop-blur-sm transition-all duration-300"
                  onClick={handleLogout}
                  disabled={loggingOut}
                >
                  {loggingOut ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white rounded-full border-t-transparent mr-2" />
                      Saindo...
                    </>
                  ) : (
                    'Sair'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Tabs defaultValue="profile" className="space-y-8">
            <TabsList className="grid w-full grid-cols-2 max-w-md mx-auto md:mx-0 bg-white/60 backdrop-blur-sm border shadow-sm">
              <TabsTrigger 
                value="profile" 
                className="data-[state=active]:bg-blue-500 data-[state=active]:text-white transition-all duration-300"
              >
                <UserIcon className="h-4 w-4 mr-2" />
                Perfil
              </TabsTrigger>
              <TabsTrigger 
                value="timezone"
                className="data-[state=active]:bg-blue-500 data-[state=active]:text-white transition-all duration-300"
              >
                <Globe className="h-4 w-4 mr-2" />
                Fuso Horário
              </TabsTrigger>
            </TabsList>

            <TabsContent value="profile" className="space-y-6">
              {/* Profile Overview Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Account Status Card */}
                <Card className="bg-white/60 backdrop-blur-sm border shadow-sm">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-3">
                      <div className={`p-3 rounded-full ${profile?.is_active ? 'bg-green-100' : 'bg-yellow-100'}`}>
                        {profile?.is_active ? (
                          <CheckCircle className="h-6 w-6 text-green-600" />
                        ) : (
                          <AlertCircle className="h-6 w-6 text-yellow-600" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Status da Conta</p>
                        <p className={`font-extrabold ${profile?.is_active ? 'text-green-600' : 'text-yellow-600'}`}>
                          {profile?.is_active ? 'Ativa' : 'Inativa'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Role Card */}
                <Card className="bg-white/60 backdrop-blur-sm border shadow-sm">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-3">
                      <div className="p-3 rounded-full bg-blue-100">
                        <UserIcon className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Função</p>
                        <p className="font-extrabold text-blue-600">
                          {profile?.role ? getRoleName(profile.role) : 'Usuário'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Created Date Card */}
                <Card className="bg-white/60 backdrop-blur-sm border shadow-sm">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-3">
                      <div className="p-3 rounded-full bg-purple-100">
                        <Calendar className="h-6 w-6 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Membro desde</p>
                        <p className="font-extrabold text-purple-600">
                          {profile?.created_at ? new Date(profile.created_at).toLocaleDateString('pt-BR') : 'N/A'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>


              </div>

              {/* Detailed Information */}
              <Card className="bg-white/60 backdrop-blur-sm border shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Settings className="h-5 w-5 mr-2" />
                    Informações Detalhadas
                  </CardTitle>
                  <CardDescription>
                    Visualize e gerencie os detalhes da sua conta
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <Mail className="h-5 w-5 text-gray-500" />
                          <div>
                            <p className="text-sm font-extrabold text-gray-900">Email</p>
                            <p className="text-sm text-gray-500">{profile?.email}</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <UserIcon className="h-5 w-5 text-gray-500" />
                          <div>
                            <p className="text-sm font-extrabold text-gray-900">ID do Usuário</p>
                            <p className="text-sm text-gray-500 font-mono">{profile?.id}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <Calendar className="h-5 w-5 text-gray-500" />
                          <div>
                            <p className="text-sm font-extrabold text-gray-900">Data de Criação</p>
                            <p className="text-sm text-gray-500">
                              {profile?.created_at ? formatDate(profile.created_at) : 'N/A'}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <Clock className="h-5 w-5 text-gray-500" />
                          <div>
                            <p className="text-sm font-extrabold text-gray-900">Última Atualização</p>
                            <p className="text-sm text-gray-500">
                              {profile?.updated_at ? formatDate(profile.updated_at) : 'N/A'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="timezone" className="space-y-6">
              <Card className="bg-white/60 backdrop-blur-sm border shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Globe className="h-5 w-5 mr-2" />
                    Configurações de Fuso Horário
                  </CardTitle>
                  <CardDescription>
                    Ajuste seu fuso horário para garantir que datas e horas sejam exibidas corretamente.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <UserTimezoneSettings />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </>
  );
} 